import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Loader2, User as UserIcon, Sparkles, X, ImagePlus, Wand2 } from 'lucide-react';
import { toast } from 'sonner';
import AuthShell from '../components/AuthShell';
import { api } from '../lib/api';
import { useAuth } from '../lib/AuthContext';

const AFRICAN_COUNTRIES = [
  'Nigeria', 'Ghana', 'Kenya', 'South Africa', 'Ethiopia', 'Tanzania', 'Uganda',
  'Cameroon', 'Senegal', 'Ivory Coast', 'DR Congo', 'Zimbabwe', 'Rwanda',
  'Somalia', 'Sudan', 'Egypt', 'Morocco', 'Algeria', 'Tunisia', 'Mozambique',
  'Angola', 'Mali', 'Burkina Faso', 'Sierra Leone', 'Liberia', 'Eritrea',
  'Botswana', 'Namibia', 'Zambia', 'Madagascar', 'Other',
];

const CANADIAN_CITIES = [
  'Toronto', 'Vancouver', 'Montreal', 'Calgary', 'Edmonton', 'Ottawa',
  'Winnipeg', 'Quebec City', 'Hamilton', 'Halifax', 'Brampton', 'Mississauga',
  'Surrey', 'Other',
];

const RELATIONSHIP_INTENTS = [
  'Serious relationship',
  'Marriage-minded',
  'Friendship leading to relationship',
  'Business & Event Buddies',
];

const GENDERS = ['Man', 'Woman', 'Non-binary'];
const PREFERENCES = ['Men', 'Women', 'Everyone'];

export default function ProfileSetup() {
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const [loading, setLoading] = useState(false);
  const [aiBusy, setAiBusy] = useState(false);

  const [form, setForm] = useState({
    name: '',
    age: '',
    gender: '',
    dating_preference: '',
    city: '',
    country_of_origin: '',
    tribe: '',
    languages: '',
    religion: '',
    relationship_intents: [],
    bio: '',
    photos: [],
  });

  function update(k, v) { setForm({ ...form, [k]: v }); }

  async function handleGenerateBio() {
    if (!form.country_of_origin || !form.city) {
      toast.error('Pick your country and city first — that helps me write a real bio.');
      return;
    }
    setAiBusy(true);
    try {
      const langArr = form.languages
        ? form.languages.split(',').map((s) => s.trim()).filter(Boolean)
        : [];
      const intentsStr = form.relationship_intents.length
        ? form.relationship_intents.join(', ')
        : '';
      const profile_hints = {
        name: form.name?.split(' ')[0] || undefined,
        age: form.age ? Number(form.age) : undefined,
        country_of_origin: form.country_of_origin,
        city: form.city,
        tribe: form.tribe || undefined,
        languages: langArr,
        religion: form.religion || undefined,
        relationship_intent: intentsStr || undefined,
      };
      const { suggestion, fallback } = await api('/ai/bio-suggestion', {
        method: 'POST',
        body: JSON.stringify({ profile_hints }),
      });
      if (form.bio.trim() && !window.confirm('Replace your current bio with the AI suggestion?')) {
        return;
      }
      update('bio', suggestion);
      if (fallback) {
        toast.message('AI was busy — used a quick template you can edit.');
      } else {
        toast.success('Bio generated! Tweak it to make it yours.');
      }
    } catch (err) {
      toast.error(err.message || 'Could not generate bio');
    } finally {
      setAiBusy(false);
    }
  }

  function toggleIntent(intent) {
    setForm((f) => ({
      ...f,
      relationship_intents: f.relationship_intents.includes(intent)
        ? f.relationship_intents.filter((x) => x !== intent)
        : [...f.relationship_intents, intent],
    }));
  }

  async function handlePhotoUpload(e) {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    const remaining = 6 - form.photos.length;
    if (remaining <= 0) { toast.error('Maximum 6 photos'); return; }
    const toRead = files.slice(0, remaining);
    const newPhotos = [];
    for (const file of toRead) {
      if (file.size > 5 * 1024 * 1024) { toast.error(`${file.name} too large (max 5MB)`); continue; }
      try {
        const dataUrl = await new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = () => resolve(reader.result);
          reader.onerror = reject;
          reader.readAsDataURL(file);
        });
        newPhotos.push(dataUrl);
      } catch { toast.error(`Failed to read ${file.name}`); }
    }
    setForm((f) => ({ ...f, photos: [...f.photos, ...newPhotos] }));
    e.target.value = '';
  }

  function removePhoto(idx) {
    setForm((f) => ({ ...f, photos: f.photos.filter((_, i) => i !== idx) }));
  }

  async function handleSubmit(e) {
    e.preventDefault();
    if (!form.name.trim() || !form.age || !form.gender || !form.dating_preference ||
        !form.city || !form.country_of_origin || !form.bio.trim()) {
      toast.error('Please fill in all required fields');
      return;
    }
    if (form.photos.length < 2) { toast.error('Please upload at least 2 photos'); return; }
    if (form.relationship_intents.length < 2) {
      toast.error('Please select at least 2 relationship intents');
      return;
    }
    if (form.bio.trim().length < 20) { toast.error('Bio must be at least 20 characters'); return; }
    if (parseInt(form.age) < 18) { toast.error('You must be at least 18 years old'); return; }

    setLoading(true);
    try {
      const data = await api('/auth/profile-setup', {
        method: 'POST',
        body: JSON.stringify({
          name: form.name.trim(),
          age: parseInt(form.age),
          gender: form.gender,
          dating_preference: form.dating_preference,
          city: form.city,
          country_of_origin: form.country_of_origin,
          tribe: form.tribe || '',
          languages: form.languages.split(',').map((l) => l.trim()).filter(Boolean),
          religion: form.religion || '',
          relationship_intents: form.relationship_intents,
          photos: form.photos,
          bio: form.bio.trim(),
        }),
      });
      if (data.user) setUser(data.user);
      toast.success('Profile complete!');
      navigate('/app/discover');
    } catch (err) {
      toast.error(err.message || 'Failed to save profile');
    } finally {
      setLoading(false);
    }
  }

  const fieldCls = 'w-full bg-white border border-[#F4F1DE] rounded-lg px-4 py-3 outline-none focus:border-[#E07A5F] text-[#2B2D42] placeholder-[#B5B5B5]';
  const labelCls = 'block text-sm font-bold text-[#2B2D42] mb-2';

  return (
    <AuthShell showBack={false}>
      <div className="flex items-center gap-2 mb-2">
        <Sparkles className="w-6 h-6 text-[#E07A5F]" />
        <span className="text-xs font-bold uppercase tracking-wider text-[#C8553D]">Final Step</span>
      </div>
      <h1 className="text-3xl font-serif font-bold text-[#2B2D42]" style={{ fontFamily: 'Playfair Display, serif' }}>
        Tell us about yourself
      </h1>
      <p className="text-[#6D6875] mt-2 mb-8">Complete your profile to start connecting</p>

      <form onSubmit={handleSubmit} className="space-y-5" data-testid="profile-setup-form">
        <div>
          <label className={labelCls}>Display name *</label>
          <input data-testid="setup-name-input" className={fieldCls} value={form.name} onChange={(e) => update('name', e.target.value)} placeholder="Your name" />
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className={labelCls}>Age *</label>
            <input data-testid="setup-age-input" type="number" min="18" max="99" className={fieldCls} value={form.age} onChange={(e) => update('age', e.target.value)} placeholder="25" />
          </div>
          <div>
            <label className={labelCls}>Gender *</label>
            <select data-testid="setup-gender-select" className={fieldCls} value={form.gender} onChange={(e) => update('gender', e.target.value)}>
              <option value="">Select...</option>
              {GENDERS.map((g) => <option key={g} value={g}>{g}</option>)}
            </select>
          </div>
        </div>

        <div>
          <label className={labelCls}>Interested in *</label>
          <select data-testid="setup-preference-select" className={fieldCls} value={form.dating_preference} onChange={(e) => update('dating_preference', e.target.value)}>
            <option value="">Select...</option>
            {PREFERENCES.map((p) => <option key={p} value={p}>{p}</option>)}
          </select>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className={labelCls}>City (Canada) *</label>
            <select data-testid="setup-city-select" className={fieldCls} value={form.city} onChange={(e) => update('city', e.target.value)}>
              <option value="">Select...</option>
              {CANADIAN_CITIES.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
          <div>
            <label className={labelCls}>Country of origin *</label>
            <select data-testid="setup-country-select" className={fieldCls} value={form.country_of_origin} onChange={(e) => update('country_of_origin', e.target.value)}>
              <option value="">Select...</option>
              {AFRICAN_COUNTRIES.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
        </div>

        <div>
          <label className={labelCls}>Looking for * <span className="text-xs font-normal text-[#B5B5B5]">(select 2 or more)</span></label>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2" data-testid="setup-intents-group">
            {RELATIONSHIP_INTENTS.map((r) => {
              const active = form.relationship_intents.includes(r);
              return (
                <button
                  type="button"
                  key={r}
                  onClick={() => toggleIntent(r)}
                  data-testid={`setup-intent-${r.replace(/[^a-z]+/gi,'-').toLowerCase()}`}
                  className={`text-left px-4 py-3 rounded-lg border transition-all text-sm font-semibold ${
                    active
                      ? 'bg-[#C8553D] border-[#C8553D] text-white shadow-lg shadow-[#C8553D]/30'
                      : 'bg-white border-[#F4F1DE] text-[#2B2D42] hover:border-[#E07A5F]'
                  }`}
                >
                  {r}
                </button>
              );
            })}
          </div>
        </div>

        <div>
          <label className={labelCls}>Tribe (optional)</label>
          <input data-testid="setup-tribe-input" className={fieldCls} value={form.tribe} onChange={(e) => update('tribe', e.target.value)} placeholder="e.g. Yoruba, Akan, Kikuyu" />
        </div>

        <div>
          <label className={labelCls}>Languages (comma separated)</label>
          <input data-testid="setup-languages-input" className={fieldCls} value={form.languages} onChange={(e) => update('languages', e.target.value)} placeholder="English, French, Yoruba" />
        </div>

        <div>
          <label className={labelCls}>Religion (optional)</label>
          <input data-testid="setup-religion-input" className={fieldCls} value={form.religion} onChange={(e) => update('religion', e.target.value)} placeholder="e.g. Christian, Muslim" />
        </div>

        <div>
          <label className={labelCls}>Photos * <span className="text-xs font-normal text-[#B5B5B5]">(2–6 photos, max 5MB each)</span></label>
          <div className="grid grid-cols-3 gap-3" data-testid="setup-photos-grid">
            {form.photos.map((src, i) => (
              <div key={i} className="relative aspect-square rounded-lg overflow-hidden border-2 border-[#F4F1DE]" data-testid={`setup-photo-${i}`}>
                <img src={src} alt={`Photo ${i + 1}`} className="w-full h-full object-cover" />
                <button type="button" onClick={() => removePhoto(i)} data-testid={`setup-remove-photo-${i}`}
                  className="absolute top-1 right-1 w-6 h-6 rounded-full bg-black/70 text-white flex items-center justify-center hover:bg-black">
                  <X className="w-3.5 h-3.5" />
                </button>
                {i === 0 && (
                  <div className="absolute bottom-1 left-1 px-2 py-0.5 bg-[#C8553D] rounded text-white text-[10px] font-bold uppercase">Main</div>
                )}
              </div>
            ))}
            {form.photos.length < 6 && (
              <label data-testid="setup-photo-upload" className="aspect-square rounded-lg border-2 border-dashed border-[#E07A5F] bg-[#FFE5DD]/30 flex flex-col items-center justify-center cursor-pointer hover:bg-[#FFE5DD]/60 transition-colors">
                <ImagePlus className="w-6 h-6 text-[#C8553D] mb-1" />
                <span className="text-xs font-bold text-[#C8553D]">Add photo</span>
                <input type="file" accept="image/*" multiple className="hidden" onChange={handlePhotoUpload} />
              </label>
            )}
          </div>
        </div>

        <div>
          <div className="flex items-center justify-between gap-2">
            <label className={labelCls}>Bio * <span className="text-xs font-normal text-[#B5B5B5]">(20–500 chars)</span></label>
            <button
              type="button"
              onClick={handleGenerateBio}
              disabled={aiBusy}
              data-testid="setup-bio-ai-button"
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-bold text-white bg-gradient-to-r from-[#E07A5F] to-[#C8553D] hover:opacity-90 disabled:opacity-60 shadow-sm"
            >
              {aiBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Wand2 className="w-3.5 h-3.5" />}
              {aiBusy ? 'Writing…' : 'Generate with AI'}
            </button>
          </div>
          <textarea data-testid="setup-bio-input" rows={5} maxLength={2000} className={fieldCls} value={form.bio} onChange={(e) => update('bio', e.target.value)} placeholder="Tell others about yourself, your interests, what you're looking for…" />
          <div className="text-xs text-right text-[#B5B5B5] mt-1">{form.bio.length}/2000</div>
        </div>

        <button type="submit" disabled={loading} data-testid="setup-submit-button"
          className="w-full bg-[#C8553D] hover:bg-[#9E3A26] disabled:opacity-60 text-white font-bold py-4 rounded-full flex items-center justify-center gap-2 shadow-lg shadow-[#C8553D]/30 mt-4">
          {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <><UserIcon className="w-5 h-5" /> Complete Profile</>}
        </button>
      </form>
    </AuthShell>
  );
}
